import React, { useEffect, useMemo, useState } from "react";
import { Save, RefreshCcw, ChevronDown, Circle, PencilLine } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import Select from "react-select";

const AssistantInstruction = () => {
    const [loadingList, setLoadingList] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);

    const [assistants, setAssistants] = useState([]); // [{settingKey, assistantId, name, instructions}]
    const [selectedKey, setSelectedKey] = useState("");

    const [detail, setDetail] = useState(null); // {settingKey, assistantId, name, instructions}
    const [draft, setDraft] = useState("");
    const [savedValue, setSavedValue] = useState("");

    const [error, setError] = useState("");

    const dirty = useMemo(() => (draft || "") !== (savedValue || ""), [draft, savedValue]);

    const loadList = async () => {
        setError("");
        setLoadingList(true);
        try {
            const res = await axiosInstance.get("/setting/openai/assistants");
            const items = Array.isArray(res.data) ? res.data : [];

            // only set setting is OPENAI_ASSISTANT_ID, OPENAI_ASSISTANT_INITIAL_SIFT_ID
            const filtered = items.filter((i) => i.settingKey.startsWith("OPENAI_ASSISTANT_ID") || i.settingKey.startsWith("OPENAI_ASSISTANT_INITIAL_SIFT_ID") || i.settingKey.startsWith("OPENAI_ASSISTANT_SEARCH_TERMS_ID") || i.settingKey.startsWith("OPENAI_ASSISTANT_AI_POSTION_ID") || i.settingKey.startsWith("OPENAI_ASSISTANT_INFLUENCE_SCORE_ID"));

            setAssistants(filtered);

            if (!selectedKey && items.length > 0) {
                setSelectedKey(items[0].settingKey);
            }

            if (selectedKey && items.length > 0 && !items.some((x) => x.settingKey === selectedKey)) {
                setSelectedKey(items[0].settingKey);
            }
        } catch (e) {
            setError(e?.response?.data?.error || "Failed to load assistants.");
            setAssistants([]);
        } finally {
            setLoadingList(false);
        }
    };

    const loadDetail = async (key) => {
        if (!key) return;
        setError("");
        setLoadingDetail(true);
        try {
            const res = await axiosInstance.get(`/setting/openai/assistants/${encodeURIComponent(key)}`);
            const d = res.data || null;
            setDetail(d);
            const instr = (d?.instructions ?? "").toString();
            setDraft(instr);
            setSavedValue(instr);
        } catch (e) {
            setError(e?.response?.data?.error || "Failed to load assistant details.");
            setDetail(null);
            setDraft("");
            setSavedValue("");
        } finally {
            setLoadingDetail(false);
        }
    };

    const save = async () => {
        if (!detail?.settingKey) return;
        setError("");
        setSaving(true);
        try {
            const res = await axiosInstance.patch(
                `/setting/openai/assistants/${encodeURIComponent(detail.settingKey)}`,
                { instructions: draft }
            );
            const d = res.data || null;
            setDetail(d);
            const instr = (d?.instructions ?? "").toString();
            setDraft(instr);
            setSavedValue(instr);

            setAssistants((prev) =>
                prev.map((a) =>
                    a.settingKey === d.settingKey
                        ? { ...a, name: d.name, instructions: d.instructions, assistantId: d.assistantId }
                        : a
                )
            );
        } catch (e) {
            setError(e?.response?.data?.error || "Failed to save instructions.");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        loadList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedKey) loadDetail(selectedKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey]);

    const selectedFromList = useMemo(
        () => assistants.find((a) => a.settingKey === selectedKey) || null,
        [assistants, selectedKey]
    );

    // --- Shared react-select style (44px controls) ---
    const selectStyles44 = {
        control: (p) => ({
            ...p,
            minHeight: 44,
            height: 44,
            borderColor: "#e2e8f0",
            boxShadow: "none",
            borderRadius: 12,
            "&:hover": { borderColor: "#cbd5e1" },
        }),
        valueContainer: (p) => ({ ...p, height: 44 }),
        input: (p) => ({ ...p, margin: 0, padding: 0 }),
        option: (p, s) => ({
            ...p,
            backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
            color: s.isSelected ? "white" : "#0f172a",
        }),
        menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
        menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    };

    return (
        <div className="w-full max-w-7xl mx-auto px-4 py-6">
            <div className="mb-4">
                <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
                    Assistant Instruction
                </h1>
                <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                    Select an assistant and update its instruction text.
                </p>
            </div>

            {error ? (
                <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                    {error}
                </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                {/* Combined top bar */}
                <div className="p-4 border-b border-slate-200">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-[280px] flex-1">
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <div className="text-sm font-extrabold text-slate-900">Assistant</div>
                                    <div className="text-xs text-slate-500">
                                        {loadingList ? "Loading..." : `${assistants.length} available`}
                                    </div>
                                </div>
                                <div className="h-9 w-9 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center">
                                    <PencilLine className="h-4 w-4 text-slate-500" />
                                </div>
                            </div>

                            <div className="mt-3">
                                <label className="mb-1 block text-xs font-semibold text-slate-600">
                                    Select assistant
                                </label>

                                <Select
                                    value={
                                        assistants.find((a) => a.settingKey === selectedKey)
                                            ? {
                                                value: selectedKey,
                                                label:
                                                    assistants.find((a) => a.settingKey === selectedKey)?.name ||
                                                    selectedKey,
                                            }
                                            : null
                                    }
                                    onChange={(opt) => setSelectedKey(opt?.value || "")}
                                    options={assistants.map((a) => ({
                                        value: a.settingKey,
                                        label: a.name || a.settingKey,
                                    }))}
                                    isDisabled={loadingList || assistants.length === 0}
                                    classNamePrefix="react-select"
                                    placeholder={loadingList ? "Loading assistants..." : "Select assistant"}
                                    styles={selectStyles44}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>

                            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                <div className="flex items-center gap-2">
                                    <Circle className="h-3.5 w-3.5 text-slate-400" />
                                    <div className="text-xs text-slate-600 truncate">
                                        <span className="font-semibold">Assistant ID:</span>{" "}
                                        {selectedFromList?.assistantId || detail?.assistantId || "—"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="min-w-[280px] flex-1">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-[280px]">
                                        <div className="text-sm font-extrabold text-slate-900 truncate">
                                            {loadingDetail
                                                ? "Loading..."
                                                : detail?.name || selectedFromList?.name || "Assistant"}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setDraft(savedValue)}
                                            disabled={!dirty || saving || loadingDetail}
                                            className={[
                                                "h-11 inline-flex items-center gap-2 rounded-full border px-4 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2",
                                                !dirty || saving || loadingDetail
                                                    ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus:ring-slate-400/40",
                                            ].join(" ")}
                                            title="Discard changes"
                                        >
                                            <RefreshCcw className="h-4 w-4" />
                                            Revert
                                        </button>

                                        <button
                                            type="button"
                                            onClick={loadList}
                                            className="h-11 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/40"
                                            disabled={loadingList}
                                            title="Refresh list"
                                        >
                                            <RefreshCcw className={`h-4 w-4 ${loadingList ? "animate-spin" : ""}`} />
                                            Refresh
                                        </button>

                                        <button
                                            type="button"
                                            onClick={save}
                                            disabled={!dirty || saving || loadingDetail || !detail?.settingKey}
                                            className={[
                                                "h-11 inline-flex items-center gap-2 rounded-full px-4 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2",
                                                !dirty || saving || loadingDetail || !detail?.settingKey
                                                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                                    : "bg-slate-900 text-white hover:bg-slate-800 focus:ring-slate-400/40",
                                            ].join(" ")}
                                            title={!dirty ? "No changes to save" : "Save instructions"}
                                        >
                                            <Save className="h-4 w-4" />
                                            {saving ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-3">
                                    {dirty ? (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                                            You have unsaved changes.
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800">
                                            Changes are saved directly to the assistant instructions.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Editor */}
                <div className="p-4">
                    <label className="block text-xs font-semibold text-slate-600 mb-2">Instructions</label>

                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Enter assistant instructions..."
                        className="min-h-[440px] w-full rounded-2xl border border-slate-300 bg-white p-4 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400/40 whitespace-pre-wrap"
                        disabled={loadingDetail || !detail?.settingKey}
                    />

                    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-slate-500">
                            <span className="font-semibold text-slate-700">{draft?.length || 0}</span> characters
                        </div>

                        <button
                            type="button"
                            onClick={save}
                            disabled={!dirty || saving || loadingDetail || !detail?.settingKey}
                            className={[
                                "h-11 inline-flex items-center gap-2 rounded-full px-4 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2",
                                !dirty || saving || loadingDetail || !detail?.settingKey
                                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                    : "bg-slate-900 text-white hover:bg-slate-800 focus:ring-slate-400/40",
                            ].join(" ")}
                        >
                            <Save className="h-4 w-4" />
                            {saving ? "Saving..." : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssistantInstruction;
